import {
  FormEvent,
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { useMakeSubscriptionPayment } from "../../../api/mutations/useMakeSubscriptionPayment";
import { useSavePayment } from "../../../api/mutations/useSavePayment";
import { useSaveUser } from "../../../api/mutations/useSaveUser";
import { useSendEmail } from "../../../api/mutations/useSendEmail";
import { Memberships } from "../../../types/Types";
import {
  logPaymentError,
  getPaymentErrorSource,
  getPaymentErrorMessage,
} from "../../../utils/paymentErrorLogger";

const CONTAINER_MIN_HEIGHT = 420;
const CONTAINER_STYLE = { minHeight: CONTAINER_MIN_HEIGHT };
const DISCOUNT_CODES: { code: string; description: string }[] = [
  { code: "24OFF", description: "24 month free" },
  { code: "DISCOUNT12", description: "12 month free" },
  { code: "WAIVE6", description: "6 month free" },
  { code: "COMP3", description: "3 month free" },
  { code: "1COMP", description: "1 month free" },
  { code: "53OFF", description: "50% off for 3 months" },
  { code: "56OFF", description: "50% off for 6 months" },
  { code: "512OFF", description: "50% off for 12 months" },
];

const CARD_ELEMENT_STYLE = {
  base: {
    fontSize: "16px" as const,
    color: "#000",
    "::placeholder": { color: "#999" },
  },
  invalid: { color: "red" as const },
};

const CARD_NUMBER_OPTIONS = {
  style: {
    base: CARD_ELEMENT_STYLE.base,
    invalid: CARD_ELEMENT_STYLE.invalid,
  },
};

const CARD_EXPIRY_OPTIONS = { style: { base: CARD_ELEMENT_STYLE.base } };
const CARD_CVC_OPTIONS = { style: { base: CARD_ELEMENT_STYLE.base } };

function getDiscountEndDate(discountCode: string): Date | null {
  const today = new Date();
  switch (discountCode) {
    case "24OFF":
      return new Date(today.setMonth(today.getMonth() + 24));
    case "DISCOUNT12":
      return new Date(today.setMonth(today.getMonth() + 12));
    case "WAIVE6":
      return new Date(today.setMonth(today.getMonth() + 6));
    case "COMP3":
      return new Date(today.setMonth(today.getMonth() + 3));
    case "1COMP":
      return new Date(today.setMonth(today.getMonth() + 1));
    case "53OFF":
      return new Date(today.setMonth(today.getMonth() + 3));
    case "56OFF":
      return new Date(today.setMonth(today.getMonth() + 6));
    case "512OFF":
      return new Date(today.setMonth(today.getMonth() + 12));
    default:
      return null;
  }
}

interface CheckoutStepProps {
  selectedMembership: string;
  isAnnualMembership: "monthly" | "annual";
  onNext: () => void;
  onBack: () => void;
  memberships: Memberships[];
  membershipPrice?: number;
  discountCode?: string;
}

function CheckoutStepInner({
  selectedMembership,
  isAnnualMembership,
  onNext,
  onBack,
  memberships,
  membershipPrice = 0,
  discountCode,
}: CheckoutStepProps) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const [billingAddress, setBillingAddress] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<Record<string, any>>({});
  const { mutate: sendMail } = useSendEmail({
    onSuccess() {},
  });

  const REGISTRATION_CONFIRMATION_KEY = "registration_show_confirmation";

  const { mutate: updateUser, isPending } = useSaveUser({
    onSuccess: () => {
      // Show Jazakallah (Confirmation) step first so user sees it even if component remounts
      sessionStorage.setItem(REGISTRATION_CONFIRMATION_KEY, "1");
      onNext();
      // Refetch user after UI has moved to step 4, so invalidation doesn't cause remount back to step 1
      if (userData?.objectId) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["user", userData.objectId] });
        }, 0);
      }
    
      let subject = "";
      let body = "";
      const firstName = userData.firstName || "";
      const membershipName = selectedMembership.toLowerCase();
    
      if (membershipName.includes("youth")) {
        subject = "Welcome to Connect by SMBF – You’re now a Youth Member";
        body = `
        <p>Assalamu Alaikum ${firstName},</p>
        <p>You’ve successfully joined <b>Connect by SMBF</b> as a <b>Youth Member</b>.</p>
        <p>This platform was built to support young Muslim entrepreneurs — people like you with big ideas, starting their journey.</p>
        <p>Start by completing your profile. Then explore events, forums, and member connections.</p>
        <p style="text-align:center;">
          <a href="https://www.connect.smbf.global/login" target="_blank" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); color:#fff; padding:12px 28px; text-decoration:none; border-radius:6px; display:inline-block;">Login Now</a>
        </p>
        `;
      } else if (membershipName.includes("business")) {
        subject = `Welcome ${firstName} – Your Business Membership is active`;
        body = `
        <p>Assalamu Alaikum ${firstName},</p>
        <p>Thanks for joining <b>Connect by SMBF</b> as a <b>Business Member</b>.</p>
        <p>You’ve now unlocked access to Muslim-owned businesses across 12+ countries — all in one trusted space.</p>
        <p>Complete your profile, browse the member directory, and start making valuable connections today.</p>
        <p style="text-align:center;">
          <a href="https://www.connect.smbf.global/login" target="_blank" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); color:#fff; padding:12px 28px; text-decoration:none; border-radius:6px; display:inline-block;">Login Now</a>
        </p>
        `;
      } else if (membershipName.includes("executive")) {
        subject = `Welcome ${firstName} – Executive Access Confirmed`;
        body = `
        <p>Assalamu Alaikum ${firstName},</p>
        <p>Your <b>Executive Membership</b> is now active.</p>
        <p>This tier is designed for businesses ready to grow nationally and internationally — with access to strategic partnerships, advanced filters, and high-value introductions.</p>
        <p>Start by setting up your profile and engaging with the community.</p>
        <p style="text-align:center;">
          <a href="https://www.connect.smbf.global/login" target="_blank" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); color:#fff; padding:12px 28px; text-decoration:none; border-radius:6px; display:inline-block;">Login Now</a>
        </p>
        `;
      } else {
        // fallback (in case membership name doesn’t match)
        subject = "Welcome to Connect by SMBF";
        body = `
        <p>Assalamu Alaikum ${firstName},</p>
        <p>Welcome to <b>Connect by SMBF</b>. We're thrilled to have you on board!</p>
        <p style="text-align:center;">
          <a href="https://www.connect.smbf.global/login" target="_blank" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); color:#fff; padding:12px 28px; text-decoration:none; border-radius:6px; display:inline-block;">Login Now</a>
        </p>
        `;
      }
    
      const fullHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>${subject}</title>
      </head>
      <body style="margin:0; padding:0; font-family:Arial, Helvetica, sans-serif; background-color:#f4f6f8;">
        <table width="100%" cellspacing="0" cellpadding="0" bgcolor="#f4f6f8">
          <tr>
            <td align="center" style="padding:40px 20px;">
              <table width="600" cellspacing="0" cellpadding="0" bgcolor="#ffffff" style="border-radius:10px; border:1px solid #e0e0e0; overflow:hidden;">
                <tr>
                  <td align="center" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); padding:30px;">
                    <img src="https://drive.google.com/uc?export=view&id=17bPec3b1DFm41gg757oKNyTmP1942MOg" alt="Connect by SMBF" width="180" style="display:block; margin:0 auto;" />
                  </td>
                </tr>
                <tr>
                  <td style="padding:35px; font-size:15px; color:#333; line-height:1.6;">
                    ${body}
                    <p style="margin:30px 0 0; font-size:13px; color:#555;">Best regards,<br/>The SMBF Team</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td align="center" style="padding:20px; font-size:11px; color:#888;">
              &copy; 2025 SMBF. All rights reserved.
            </td>
          </tr>
        </table>
      </body>
      </html>
      `;
    
      sendMail({
        email: userData.email,
        subject,
        body: fullHTML,
        name: `${userData.firstName} ${userData.lastName}`,
      });
    },
    
  });

  const { mutate } = useMakeSubscriptionPayment();
  const { mutate: savePaymentMethod } = useSavePayment();

  useEffect(() => {
    const storedData = localStorage.getItem("UserData");
    const mergedData = storedData
      ? { ...JSON.parse(storedData), ...userData }
      : userData;
    setUserData(mergedData);
    if (storedData) {
      setUserData(JSON.parse(storedData));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate from localStorage only on mount; userData in deps would re-trigger when we set it
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!stripe || !elements) {
        const msg = "Stripe.js has not loaded yet.";
        logPaymentError({
          flow: "membership",
          errorSource: "frontend",
          message: msg,
        });
        setError(msg);
        return;
      }

      setLoading(true);
      setError("");
      const selected = memberships.find((m) => m.name === selectedMembership);
      if (!selected) {
        const msg = "Invalid membership selection.";
        logPaymentError({
          flow: "membership",
          errorSource: "frontend",
          message: msg,
          requestPayload: { selectedMembership, membershipCount: memberships.length },
        });
        setError(msg);
        setLoading(false);
        return;
      }

      try {
        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
          const msg = "Card input not found.";
          logPaymentError({
            flow: "membership",
            errorSource: "frontend",
            message: msg,
          });
          setError(msg);
          setLoading(false);
          return;
        }
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardNumberElement,
        });
        mutate(
          {
            productId:
              isAnnualMembership === "annual"
                ? selected?.productIdAnnual
                : selected?.productId || "",
            userId: userData.objectId || "",
            paymentMethodId: paymentMethod?.id,
            discountCode: discountCode || "",
            name: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
            email: userData.email || "",
          },
          {
            onSuccess: async (response) => {
              const clientSecret = response.clientSecret;
              if (!clientSecret) {
                const msg = "No client secret returned from server.";
                logPaymentError({
                  flow: "membership",
                  errorSource: "backend",
                  message: msg,
                  responseData: response,
                });
                setError(msg);
                setLoading(false);
                return;
              }

              const returnUrl = `${window.location.origin}${window.location.pathname}`;
              const { setupIntent, error: confirmError } =
                await stripe.confirmCardSetup(clientSecret, {
                  payment_method: paymentMethod?.id,
                  return_url: returnUrl,
                });

             if (confirmError) {
  const isAuthFailure =
    confirmError.code === "setup_intent_authentication_failure";

  const msg = isAuthFailure
    ? "Authentication was cancelled or failed. Please try again or use another card."
    : confirmError.message || "Card setup confirmation failed.";

  logPaymentError({
    flow: "membership",
    errorSource: "stripe",
    message: msg,
    error: confirmError,
  });

  setError(msg);
  setLoading(false);
  return;
}

              if (setupIntent && setupIntent.status === "succeeded") {
                const formData = new FormData();
                Object.entries(userData).forEach(([key, value]) => {
                  if (value !== undefined && value !== null) {
                    formData.append(key, String(value));
                  }
                });

                formData.append("hasPaid", "1");
                if (userData.discountCode) {
                  const endDate = getDiscountEndDate(userData.discountCode);
                  if (endDate) {
                    formData.append("discountEndDate", endDate.toISOString());
                  }
                }

                if (saveCard) {
                  savePaymentMethod({
                    userId: userData.objectId || "",
                    customerId: response.customerId || "",
                    paymentMethodId: paymentMethod?.id,
                    billingAddress,
                    brand: paymentMethod?.card?.brand || "",
                    last4: paymentMethod?.card?.last4 || "",
                  });
                }

                updateUser(formData);
              }

              setLoading(false);
            },
            onError: (err: unknown) => {
              const source = getPaymentErrorSource(err);
              const msg = getPaymentErrorMessage(err);
              logPaymentError({
                flow: "membership",
                errorSource: source,
                message: msg,
                error: err,
                responseStatus:
                  err && typeof err === "object" && "response" in err
                    ? (err as { response?: { status?: number } }).response
                        ?.status
                    : undefined,
                responseData:
                  err && typeof err === "object" && "response" in err
                    ? (err as { response?: { data?: unknown } }).response?.data
                    : undefined,
              });
              setError(msg);
              setLoading(false);
            },
          }
        );
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Unexpected error occurred.";
        logPaymentError({
          flow: "membership",
          errorSource: "frontend",
          message: msg,
          error: err,
        });
        setError(msg);
        setLoading(false);
      }
    },
    [
      stripe,
      elements,
      memberships,
      selectedMembership,
      isAnnualMembership,
      userData,
      discountCode,
      saveCard,
      billingAddress,
      mutate,
      updateUser,
      savePaymentMethod,
    ]
  );

  return (
    <form onSubmit={handleSubmit} className="w-100" autoComplete="off">
      <div
        className="d-flex flex-column gap-3"
        style={CONTAINER_STYLE}
      >
        <div className="d-flex justify-content-between">
          <h5>Checkout</h5>
          <h5>
            {isAnnualMembership === "annual"
              ? "Annual Membership"
              : "Monthly Membership"}
          </h5>
        </div>
        <div className="d-flex justify-content-between px-2">
          <span className="fw-medium">{selectedMembership}</span>
          <span className="fw-bold">£{membershipPrice.toFixed(2)}</span>
        </div>

        {discountCode &&
          !(discountCode === "512OFF" && isAnnualMembership === "monthly") && (
            <>
              <div className="d-flex justify-content-between px-2">
                <span className="fw-medium">Discount Code</span>
                <span className="fw-bold">{discountCode}</span>
              </div>
              <div className="d-flex justify-content-between px-2">
                <span className="fw-medium">Discount Applied</span>
                <span className="fw-bold">
                  {DISCOUNT_CODES.find((dc) => dc.code === discountCode)
                    ?.description || "No discount"}
                </span>
              </div>
            </>
          )}

        <hr />

        <div className="row g-2">
          <div className="col-md-6 mb-3">
            <label>Card Number</label>
            <div className="form-control">
              <CardNumberElement options={CARD_NUMBER_OPTIONS} />
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <label>Expiry</label>
            <div className="form-control">
              <CardExpiryElement options={CARD_EXPIRY_OPTIONS} />
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <label>CVC</label>
            <div className="form-control">
              <CardCvcElement options={CARD_CVC_OPTIONS} />
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <label>Billing Address (optional)</label>
            <input
              type="text"
              className="form-control"
              placeholder="Billing Address"
              value={billingAddress}
              autoComplete="new-address"
              onChange={(e) => setBillingAddress(e.target.value)}
            />
          </div>

          <div className="col-md-12 mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="saveCard"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="saveCard">
                Save this card for future payments
              </label>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-2 px-3" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onBack}
            disabled={loading}
          >
            Back
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!stripe || loading || isPending}
          >
            {loading || isPending ? "Processing..." : "Pay Now"}
          </button>
        </div>
      </div>
    </form>
  );
}

CheckoutStepInner.displayName = "CheckoutStep";

const CheckoutStep = memo(CheckoutStepInner);
CheckoutStep.displayName = "CheckoutStep";

export default CheckoutStep;
